import React from "react";
import ReactDOM from "react-dom";
import {Provider} from "unstated";
import {BrowserRouter, Switch} from "react-router-dom";
import ThemeWrapper from "./themes/ThemeWrapper";
import noty from "./lib/noty";
import {getToken} from "./lib/auth";
import {renderAvailableRoutes, renderPublicRoutes} from "./routing";
import feathers from "./feathers";
import registerServiceWorker from "./registerServiceWorker";
import {UserAccountContext} from "./contexts/userAccount";
import {TrainingContext} from "./contexts/training";

import {
  BuildingRecord,
  CurrentAccountRecord,
  FacilityRecord,
  FloorRecord,
  GroupRecord,
  InvitationRecord,
  MarkerRecord,
  MarkerTypeCategoryRecord,
  MarkerTypeRecord,
  PermissionRecord,
  PlanRecord,
  TrainingRecord,
  UserRecord
} from "./feathers/records";
import {appContainer} from "./contexts/app";
import SubscriptionStatus from "./components/SubscriptionStatus";
import "flexboxgrid";
import "./style.scss";

import ConfirmModal from "./components/ConfirmModal";
import TrainingModal from "./components/TrainingModal";
import LoadingIndicator from "./components/LoadingIndicator";

export class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      account: null,
      subscriptionStatus: {
        active: true
      },
      appReady: false,
      trainingModalOpen: false,
      authenticated: false,
      targetPath: null
    };

    this.handleCheckSubscription = this.handleCheckSubscription.bind(this);
    this.toggleTrainingModal = this.toggleTrainingModal.bind(this);
    this.prepApp = this.prepApp.bind(this);
    this.loggedIn = this.loggedIn.bind(this);
  }

  async componentDidMount() {
    let appState = await this.prepApp();
    this.setState({...appState, appReady: true});
  }

  async prepApp() {
    let authenticated = await this.checkAuthenticated();
    if (!authenticated) {
      return {authenticated};
    }
    let userState = await this.fetchUser();
    await this.fetchAllTheThings();
    return {authenticated, ...userState};
  }

  async checkAuthenticated() {
    if (!getToken()) {
      return false;
    }

    let result = true;
    try {
      if (!appContainer.state.get("authenticated")) {
        await feathers._authenticate();
      }
    } catch (e) {
      result = false;
    }
    return result;
  }

  async getSubscriptionStatus(currentAccount) {
    let subscriptionServiceInfo = await feathers.service("api/subscription-status").get(currentAccount._id);

    return new SubscriptionStatus(subscriptionServiceInfo);
  }

  async fetchUser() {
    let [currentAccount] = await Promise.all([
      CurrentAccountRecord.getCurrentAccount(),
      CurrentAccountRecord.loadSessions(),
      UserRecord.getCurrentUser()
    ]);

    const subscriptionStatus = await this.getSubscriptionStatus(currentAccount);
    let currentSession = appContainer.state.getIn(["sessions", currentAccount._id]);

    appContainer.set("ownerName", currentSession.get("owner_name"));
    appContainer.set("trialWarningOpen", subscriptionStatus.trialWarning);
    return {subscriptionStatus, account: currentAccount};
  }

  async fetchAllTheThings() {
    await Promise.all([
      BuildingRecord.find(),
      FacilityRecord.find(),
      FloorRecord.find(),
      MarkerTypeCategoryRecord.find(),
      MarkerTypeRecord.find(),
      MarkerRecord.find(),
      UserRecord.find(),
      InvitationRecord.find(),
      GroupRecord.find({paginate: false}),
      PermissionRecord.find(),
      PlanRecord.find(),
      TrainingRecord.find()
    ]);
  }

  render() {
    if (!this.state.appReady) {
      return <LoadingIndicator />;
    }
    let {account, subscriptionStatus} = this.state;
    const {trainingModalOpen} = this.state;
    const userAccountCtx = {account, subscriptionStatus, onUpdateSubscription: this.handleCheckSubscription};
    return (
      <Provider>
        <UserAccountContext.Provider value={userAccountCtx}>
          <TrainingContext.Provider value={{onToggleTrainingModal: this.toggleTrainingModal}}>
            <ThemeWrapper>
              <BrowserRouter basename="/" getUserConfirmation={this.getUserConfirmation}>
                <div>
                  <Switch>{this.renderMyRoutes()}</Switch>
                </div>
              </BrowserRouter>
              <ConfirmModal />
              {trainingModalOpen && <TrainingModal onClose={this.toggleTrainingModal} />}
            </ThemeWrapper>
          </TrainingContext.Provider>
        </UserAccountContext.Provider>
      </Provider>
    );
  }

  async loggedIn(targetPath) {
    this.setState({appReady: false});
    let appState = await this.prepApp();
    this.setState({...appState, targetPath, appReady: true});
  }

  async handleCheckSubscription() {
    const {account} = this.state;

    const subscriptionStatus = await this.getSubscriptionStatus(account);
    this.setState({subscriptionStatus});
  }

  getUserConfirmation(message, callback) {
    noty.confirm(message, {
      onOk: () => {
        callback(true);
      },
      onCancel: () => {
        callback(false);
      }
    });
  }

  renderMyRoutes() {
    return this.state.authenticated
      ? renderAvailableRoutes(this.state.subscriptionStatus.active, this.state.targetPath)
      : renderPublicRoutes(this.loggedIn);
  }

  toggleTrainingModal() {
    this.setState({trainingModalOpen: !this.state.trainingModalOpen});
  }
}

ReactDOM.render(<App />, document.getElementById("root"));
registerServiceWorker();



// WEBPACK FOOTER //
// ./src/index.js